import { useLogin } from '@refinedev/core';
import { useForm } from '@refinedev/react-hook-form';
import { useState, useEffect, useCallback } from 'react';

interface LoginFormData {
	email: string;
	password: string;
	rememberMe: boolean;
}

export const LoginPage = () => {
	const { mutate: login } = useLogin();
	const [loading, setLoading] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		clearErrors,
		watch,
	} = useForm({
		defaultValues: {
			email: '',
			password: '',
			rememberMe: true,
		},
	});

	// Watch form fields for validation feedback
	const email = watch('email');
	const password = watch('password');

	// Clear errors when user starts typing
	useEffect(() => {
		if (errors.root) {
			clearErrors('root');
		}
	}, [email, password, clearErrors, errors.root]);

	const onSubmit = useCallback(
		async (data: LoginFormData) => {
			setLoading(true);
			clearErrors();

			login(
				{
					...data,
					rememberMe: data.rememberMe,
				},
				{
					onSuccess: () => {
						setLoading(false);
					},
					onError: (error) => {
						setLoading(false);
						setError('root', {
							message: error?.message || 'Login failed. Please try again.',
						});
					},
				}
			);
		},
		[login, clearErrors, setError]
	);

	const togglePasswordVisibility = useCallback(() => {
		setShowPassword((prev) => !prev);
	}, []);

	return (
		<div className='min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-white to-cyan-50 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden'>
			{/* Background decoration */}
			<div className='absolute inset-0 overflow-hidden'>
				<div className='absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full opacity-30 animate-pulse'></div>
				<div className='absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-cyan-200 to-blue-200 rounded-full opacity-30 animate-pulse delay-1000'></div>
			</div>

			<div className='max-w-md w-full space-y-8 relative z-10'>
				{/* Header */}
				<div className='text-center'>
					<div className='mx-auto h-16 w-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg transform hover:scale-105 transition-all duration-300'>
						<svg
							className='h-8 w-8 text-white'
							fill='none'
							stroke='currentColor'
							viewBox='0 0 24 24'
							aria-hidden='true'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
							/>
						</svg>
					</div>
					<h2 className='mt-6 text-center text-4xl font-bold tracking-tight bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent'>
						Welcome Back
					</h2>
					<p className='mt-2 text-center text-base text-gray-600'>
						Sign in to access your admin dashboard
					</p>
				</div>

				{/* Login Form */}
				<div className='bg-white/70 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8 transform hover:shadow-2xl transition-all duration-300'>
					{/* Error Alert */}
					{errors.root && (
						<div className='mb-6 bg-red-50 border border-red-200 rounded-xl p-4 animate-slide-down'>
							<div className='flex items-center'>
								<svg
									className='h-5 w-5 text-red-400 mr-2'
									fill='currentColor'
									viewBox='0 0 20 20'>
									<path
										fillRule='evenodd'
										d='M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z'
										clipRule='evenodd'
									/>
								</svg>
								<p className='text-sm text-red-700 font-medium'>
									{errors.root.message as string}
								</p>
							</div>
						</div>
					)}

					<form
						className='space-y-6'
						onSubmit={handleSubmit(onSubmit)}
						noValidate>
						{/* Email Field */}
						<div className='group'>
							<label
								htmlFor='email'
								className='block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors'>
								Email address
							</label>
							<div className='relative'>
								<input
									{...register('email', {
										required: 'Email is required',
										pattern: {
											value: /^\S+@\S+$/i,
											message: 'Please enter a valid email address',
										},
									})}
									type='email'
									autoComplete='email'
									className={`appearance-none relative block w-full pl-12 pr-4 py-4 border-2 ${
										errors.email
											? 'border-red-300 focus:border-red-500 focus:ring-red-500'
											: 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500'
									} placeholder-gray-400 text-gray-900 rounded-xl focus:outline-none focus:ring-2 bg-gray-50/50 transition-all duration-200 hover:bg-white`}
									placeholder='Enter your email address'
									aria-invalid={errors.email ? 'true' : 'false'}
									aria-describedby={errors.email ? 'email-error' : undefined}
								/>
								<div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
									<svg
										className='h-5 w-5 text-gray-400'
										fill='none'
										stroke='currentColor'
										viewBox='0 0 24 24'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207'
										/>
									</svg>
								</div>
								{errors.email && (
									<div className='absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none'>
										<svg
											className='h-5 w-5 text-red-500'
											fill='currentColor'
											viewBox='0 0 20 20'>
											<path
												fillRule='evenodd'
												d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
												clipRule='evenodd'
											/>
										</svg>
									</div>
								)}
							</div>
							{errors.email && (
								<p
									id='email-error'
									className='mt-2 text-sm text-red-600 animate-slide-down'
									role='alert'>
									{errors.email.message as string}
								</p>
							)}
						</div>

						{/* Password Field */}
						<div className='group'>
							<label
								htmlFor='password'
								className='block text-sm font-semibold text-gray-700 mb-2 group-focus-within:text-indigo-600 transition-colors'>
								Password
							</label>
							<div className='relative'>
								<input
									{...register('password', {
										required: 'Password is required',
										minLength: {
											value: 6,
											message: 'Password must be at least 6 characters',
										},
									})}
									type={showPassword ? 'text' : 'password'}
									autoComplete='current-password'
									className={`appearance-none relative block w-full px-4 py-4 pl-12 pr-12 border-2 ${
										errors.password
											? 'border-red-300 focus:border-red-500 focus:ring-red-500'
											: 'border-gray-200 focus:border-indigo-500 focus:ring-indigo-500'
									} placeholder-gray-400 text-gray-900 rounded-xl focus:outline-none focus:ring-2 bg-gray-50/50 transition-all duration-200 hover:bg-white`}
									placeholder='Enter your password'
									aria-invalid={errors.password ? 'true' : 'false'}
									aria-describedby={
										errors.password ? 'password-error' : undefined
									}
								/>
								<div className='absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none'>
									<svg
										className='h-5 w-5 text-gray-400'
										fill='none'
										stroke='currentColor'
										viewBox='0 0 24 24'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
										/>
									</svg>
								</div>
								<button
									type='button'
									className='absolute inset-y-0 right-0 pr-4 flex items-center hover:text-indigo-600 transition-colors'
									onClick={togglePasswordVisibility}
									aria-label={showPassword ? 'Hide password' : 'Show password'}>
									{showPassword ? (
										<svg
											className='h-5 w-5 text-gray-400 hover:text-gray-600'
											fill='none'
											stroke='currentColor'
											viewBox='0 0 24 24'>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={2}
												d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21'
											/>
										</svg>
									) : (
										<svg
											className='h-5 w-5 text-gray-400 hover:text-gray-600'
											fill='none'
											stroke='currentColor'
											viewBox='0 0 24 24'>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={2}
												d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
											/>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={2}
												d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
											/>
										</svg>
									)}
								</button>
							</div>
							{errors.password && (
								<p
									id='password-error'
									className='mt-2 text-sm text-red-600 animate-slide-down'
									role='alert'>
									{errors.password.message as string}
								</p>
							)}
						</div>

						{/* Remember Me */}
						<div className='flex items-center justify-between'>
							<div className='flex items-center'>
								<input
									{...register('rememberMe')}
									id='remember-me'
									type='checkbox'
									className='h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded transition-colors'
								/>
								<label
									htmlFor='remember-me'
									className='ml-2 block text-sm text-gray-700'>
									Remember me
								</label>
							</div>
						</div>

						{/* Submit Button */}
						<div>
							<button
								type='submit'
								disabled={loading}
								className={`group relative w-full flex justify-center py-4 px-4 border border-transparent text-sm font-semibold rounded-xl text-white transition-all duration-300 transform ${
									loading
										? 'bg-gray-400 cursor-not-allowed'
										: 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
								}`}>
								{loading ? (
									<div className='flex items-center'>
										<svg
											className='animate-spin -ml-1 mr-3 h-5 w-5 text-white'
											fill='none'
											viewBox='0 0 24 24'>
											<circle
												className='opacity-25'
												cx='12'
												cy='12'
												r='10'
												stroke='currentColor'
												strokeWidth='4'></circle>
											<path
												className='opacity-75'
												fill='currentColor'
												d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
										</svg>
										Signing in...
									</div>
								) : (
									<div className='flex items-center'>
										<svg
											className='w-5 h-5 mr-2 group-hover:translate-x-1 transition-transform'
											fill='none'
											stroke='currentColor'
											viewBox='0 0 24 24'>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={2}
												d='M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1'
											/>
										</svg>
										Sign in to Dashboard
									</div>
								)}
							</button>
						</div>
					</form>
				</div>

				{/* Footer */}
				<div className='text-center'>
					<p className='text-sm text-gray-500'>
						Secure admin access • Protected by encryption
					</p>
				</div>
			</div>

			{/* Custom animations */}
			<style>{`
				@keyframes slide-down {
					from {
						opacity: 0;
						transform: translateY(-10px);
					}
					to {
						opacity: 1;
						transform: translateY(0);
					}
				}
				.animate-slide-down {
					animation: slide-down 0.2s ease-out;
				}
			`}</style>
		</div>
	);
};
